import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Loader2,
  Check,
  KeyRound,
  Play,
  Ban,
  Save,
  AlertCircle,
} from 'lucide-react';
import { showToast } from '../../context/ToastContext';
import {
  getUserDetailService,
  updateUserService,
  updateUserPasswordService,
  toggleUserStatusService,
} from '../../service/user';
import type { IUser } from '@tool-no-hu/shared';
import { UserStatus } from '@tool-no-hu/shared/enums';
import { useConfirm } from '../../context/ConfirmContext';

export const AdminAccountDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Editable fields
  const [note, setNote] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const found = await getUserDetailService(id);
        setUser(found || null);
        if (found) {
          setNote(found.note ?? '');
        }
      } catch (err) {
        console.error('Failed to fetch user:', err);
        showToast.error('Không thể tải thông tin tài khoản');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !id) return;
    setSaving(true);
    try {
      const updated = await updateUserService(id, {
        note,
      });
      setUser(updated as IUser);
      showToast.success('Cập nhật tài khoản thành công');
    } catch (err) {
      console.error('Failed to update user:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user || !id) return;
    if (!newPassword) {
      showToast.error('Vui lòng nhập mật khẩu mới');
      return;
    }
    const isConfirmed = await confirm({
      title: 'Cấp mật khẩu mới',
      message: `Cấp mật khẩu mới cho "${user.username}" ngay lập tức?`,
    });
    if (!isConfirmed) return;

    setResetting(true);
    try {
      await updateUserPasswordService(id, newPassword);
      setNewPassword('');
      showToast.success('Đã cấp mật khẩu mới thành công');
    } catch (err) {
      console.error('Failed to reset password:', err);
    } finally {
      setResetting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!user || !id) return;
    const newStatus = user.status === 'INACTIVE' ? 'ACTIVE' : 'INACTIVE';
    const actionText = newStatus === 'INACTIVE' ? 'dừng hoạt động' : 'kích hoạt lại';
    const isConfirmed = await confirm({
      title: 'Thay đổi trạng thái',
      message: `Bạn có chắc muốn ${actionText} tài khoản "${user.username}"?`,
    });
    if (!isConfirmed) return;

    try {
      const updated = await toggleUserStatusService(id, newStatus);
      setUser(updated as IUser);
      if (updated) {
        setNote(updated.note ?? '');
      }
      showToast.success(`Đã ${actionText} tài khoản thành công`);
    } catch (err) {
      console.error('Failed to toggle user status:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-[#3b82f6]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-4 text-[#e2e8f0]">
        <button
          type="button"
          onClick={() => navigate('/admin/accounts')}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-bold text-[#94a3b8] transition-colors hover:text-[#e2e8f0]"
        >
          <ArrowLeft size={16} />
          Quay lại
        </button>
        <div className="flex flex-col items-center gap-2 rounded-xl border border-[#252a38] bg-[#161920] px-5 py-14 text-center">
          <AlertCircle size={36} className="text-[#475569]" />
          <p className="text-[14px] font-semibold text-[#475569]">Không tìm thấy tài khoản.</p>
          <button
            type="button"
            onClick={() => navigate('/admin/accounts')}
            className="mt-4 rounded-lg bg-[#3b82f6] px-4 py-2 text-[13px] font-bold text-white transition-colors hover:bg-[#2563eb]"
          >
            Quay lại danh sách
          </button>
        </div>
      </div>
    );
  }

  const inactive = user.status === UserStatus.INACTIVE;

  return (
    <div className="space-y-4 text-[#e2e8f0]">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/admin/accounts')}
        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-bold text-[#94a3b8] transition-colors hover:text-[#e2e8f0]"
      >
        <ArrowLeft size={16} />
        Quay lại
      </button>

      {/* Page Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3.5">
          <div className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#2d4a8a] bg-[#1e2a4a]">
            <User size={20} className="text-[#3b82f6]" />
          </div>
          <div>
            <h1 className="m-0 text-[18px] font-bold text-[#e2e8f0]">
              Chi tiết tài khoản
            </h1>
            <p className="m-0 mt-0.5 text-[13px] text-[#64748b]">
              #{user.username}
            </p>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${
            inactive
              ? 'border-[#7f1d1d] bg-[#1c0a0a] text-[#ef4444]'
              : 'border-[#14532d] bg-[#0d1f14] text-[#22c55e]'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              inactive ? 'bg-[#ef4444]' : 'bg-[#22c55e]'
            }`}
          />
          {inactive ? 'Đã khoá' : 'Hoạt động'}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left: Info card */}
        <div className="lg:col-span-2">
          <form
            onSubmit={handleSave}
            className="space-y-5 rounded-xl border border-[#252a38] bg-[#161920] p-6"
          >
            <h2 className="text-[15px] font-bold text-[#e2e8f0]">
              Thông tin tài khoản
            </h2>

            {/* Username (read-only) */}
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748b]">
                Tên người dùng
              </label>
              <input
                type="text"
                value={user.username}
                disabled
                className="w-full cursor-not-allowed rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-2.5 text-[13px] font-semibold text-[#475569] outline-none"
              />
            </div>

            {/* Note */}
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748b]">
                Ghi chú
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Ghi chú thêm về tài khoản..."
                className="w-full resize-none rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
              />
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-3 border-t border-[#252a38] pt-4">
              <button
                type="button"
                onClick={handleToggleStatus}
                className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-[13px] font-bold text-white transition-colors ${
                  inactive
                    ? 'bg-[#22c55e] hover:bg-[#16a34a]'
                    : 'bg-[#ef4444] hover:bg-[#dc2626]'
                }`}
              >
                {inactive ? <Play size={14} /> : <Ban size={14} />}
                {inactive ? 'Kích hoạt lại' : 'Dừng hoạt động'}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#3b82f6] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:bg-[#1e2235] disabled:text-[#334155]"
              >
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Đang lưu...
                  </>
                ) : (
                  <>
                    <Save size={14} />
                    Lưu thay đổi
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Right: Reset password card */}
        <div className="lg:col-span-1">
          <div className="space-y-5 rounded-xl border border-[#252a38] bg-[#161920] p-6">
            <div>
              <h2 className="text-[15px] font-bold text-[#e2e8f0]">
                Cấp mật khẩu mới
              </h2>
              <p className="mt-1 text-[12px] text-[#64748b]">
                Mật khẩu mới sẽ được áp dụng ngay lập tức cho người dùng.
              </p>
            </div>

            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748b]">
                Mật khẩu mới
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nhập mật khẩu mới"
                className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
              />
            </div>

            <button
              type="button"
              onClick={handleResetPassword}
              disabled={resetting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#252a38] bg-[#10131a] px-4 py-2.5 text-[13px] font-bold text-[#e2e8f0] transition-colors hover:border-[#3b82f6] hover:bg-[#1e2a4a] hover:text-[#3b82f6] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {resetting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Đang cấp...
                </>
              ) : (
                <>
                  <KeyRound size={14} />
                  Cấp mật khẩu mới
                </>
              )}
            </button>

            {/* Extra info */}
            <div className="border-t border-[#252a38] pt-4">
              <div className="space-y-2 text-[13px]">
                <div className="flex items-center justify-between">
                  <span className="text-[#64748b]">Trạng thái</span>
                  <span className="font-bold text-[#e2e8f0]">
                    {inactive ? 'Đã khoá' : 'Hoạt động'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAccountDetail;
